/**
 * What happens when someone calls one of the account's numbers, as an ordered
 * list of steps.
 *
 * The single source of truth for every place Call Settings explains routing:
 * the path chips on the overview, the trace on a number's Overview tab, and the
 * "Iris won't answer these numbers" note. If they each worked it out, they
 * would disagree the first time the calling service changed.
 *
 * Framework-free on purpose — no React, no app imports — so the mobile app can
 * take this file as it is.
 *
 * The order mirrors the calling service's inbound ladder
 * (telnyxWebhookController.js, "WHICH MECHANISM ANSWERS THIS CALL"):
 *
 *   1. A call flow published on the number owns the call outright, including
 *      its own hours. Nothing below runs.
 *   2. Closed hours or a holiday. A phone menu's own schedule, when enabled,
 *      replaces the account-wide one.
 *   3. The number's own assignment. With somebody assigned and reachable it
 *      rings them and skips the menu and Iris. With nobody reachable it either
 *      goes to voicemail or falls back to ringing everyone, as configured.
 *   4. The phone menu (extensions live here).
 *   5. Iris.
 *   6. Everyone with incoming calls on, plus the desk phones for this number.
 *   7. Nobody answers.
 *
 * "Same as another number" routes with THAT number's flow, assignment and desk
 * phones — one hop only, as the calling service reads it.
 */

export type RoutingStepKey = 'flow' | 'hours' | 'whoRings' | 'menu' | 'iris' | 'noAnswer';

/**
 * - `active`  — this step is what answers or rings the call.
 * - `pass`    — this step is on, but only sometimes takes the call (phone hours
 *               outside opening times, Iris handing a caller over).
 * - `skipped` — this step does not run for this number; `skipReason` says why.
 */
export type RoutingStepState = 'active' | 'pass' | 'skipped';

/** The Call Settings tab where a step is configured. */
export type RoutingTarget = 'general' | 'numbers' | 'flows' | 'team' | 'iris';

export interface RoutingStep {
  key: RoutingStepKey;
  state: RoutingStepState;
  title: string;
  detail: string;
  skipReason?: string;
  /** A short label for the path chips on the overview. */
  chip: string;
  target: RoutingTarget;
}

export interface RoutingNumberInput {
  id: string;
  phoneNumber: string;
  routing?: {
    enabled?: boolean;
    assignedUserIds?: string[];
    whenUnavailable?: 'ring_everyone' | 'voicemail';
    followsNumber?: string | null;
  } | null;
}

export interface RoutingFlowStepInput {
  id: string;
  type: string;
  /** Branch key → the id of the step it leads to. */
  next?: Record<string, string>;
}

export interface RoutingFlowInput {
  id: string;
  name: string;
  status: 'draft' | 'live' | 'archived';
  phoneNumbers: string[];
  /** Needed to say how the flow ends; without them it is "the flow decides". */
  entryStepId?: string | null;
  steps?: RoutingFlowStepInput[];
}

export interface RoutingUserInput {
  id: string;
  name?: string;
  incomingCallEnabled: boolean;
}

export interface RoutingDeskPhoneInput {
  id: string;
  /** Empty means every number. */
  ringsForNumbers: string[];
}

export interface RoutingContext {
  /** Every number on the account — needed to resolve "same as another number". */
  numbers: RoutingNumberInput[];
  flows: RoutingFlowInput[];
  /** Account-wide phone hours. */
  phoneHours: { enabled: boolean };
  /** The account's active phone menu, if there is one. */
  menu: { active: boolean; name?: string; hoursEnabled?: boolean };
  iris: { enabled: boolean };
  /**
   * The calling roster. `null` when the viewer can't see it: then assignment is
   * taken at face value, because "nobody reachable" can't be known.
   */
  users: RoutingUserInput[] | null;
  deskPhones: RoutingDeskPhoneInput[];
  /**
   * Whether an unanswered call reaches voicemail. On notifi-2.0 accounts the
   * calling service hangs up after the ring timeout — voicemail and the
   * missed-call text are only wired for legacy accounts — so this is false
   * there, and the trace says so rather than promising a voicemail that never
   * plays.
   */
  voicemailAfterNoAnswer: boolean;
}

/** One number in any spelling: +1, 1-prefixed or national. */
export function didKey(raw?: string | null): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

export const sameDid = (a?: string | null, b?: string | null): boolean =>
  !!didKey(a) && didKey(a) === didKey(b);

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function formatNational(raw: string): string {
  const d = didKey(raw);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : raw;
}

/**
 * The number whose settings actually route this call. One hop, like the calling
 * service: a missing target, or following itself, falls back to its own.
 */
export function routingNumberFor(
  number: RoutingNumberInput,
  numbers: RoutingNumberInput[],
): { routing: RoutingNumberInput; followed: RoutingNumberInput | null } {
  const follows = number.routing?.followsNumber;
  if (!follows || sameDid(follows, number.phoneNumber)) return { routing: number, followed: null };
  const target = numbers.find((n) => sameDid(n.phoneNumber, follows));
  return target ? { routing: target, followed: target } : { routing: number, followed: null };
}

export function liveFlowFor(
  phoneNumber: string,
  flows: RoutingFlowInput[],
): RoutingFlowInput | null {
  return (
    flows.find((f) => f.status === 'live' && f.phoneNumbers.some((p) => sameDid(p, phoneNumber))) ??
    null
  );
}

/**
 * How a flow can end, from the steps reachable from its entry. Voicemail
 * anywhere on a path means an unanswered caller can still leave a message —
 * which is why "If nobody answers" is not skipped just because a flow owns
 * the call.
 */
export function flowEnding(flow: RoutingFlowInput): 'voicemail' | 'hangup' | 'unknown' {
  if (!flow.steps || !flow.entryStepId) return 'unknown';
  const byId = new Map(flow.steps.map((st) => [st.id, st]));
  const seen = new Set<string>();
  const queue = [flow.entryStepId];
  let hangup = false;
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    const st = byId.get(id);
    if (!st) continue;
    if (st.type === 'voicemail') return 'voicemail';
    if (st.type === 'hangup') hangup = true;
    queue.push(...Object.values(st.next ?? {}));
  }
  return hangup ? 'hangup' : 'unknown';
}

export function resolveRouting(number: RoutingNumberInput, ctx: RoutingContext): RoutingStep[] {
  const { routing: owner, followed } = routingNumberFor(number, ctx.numbers);
  const followedNote = followed ? ` (same as ${formatNational(followed.phoneNumber)})` : '';

  const flow = liveFlowFor(owner.phoneNumber, ctx.flows);

  const deskPhones = ctx.deskPhones.filter(
    (d) =>
      d.ringsForNumbers.length === 0 ||
      d.ringsForNumbers.some((p) => sameDid(p, owner.phoneNumber)),
  ).length;
  const deskText = deskPhones > 0 ? ` + ${plural(deskPhones, 'desk phone')}` : '';

  // Assignment, as the calling service reads it: on with nobody picked rings
  // everyone, and it narrows to people whose incoming calls are on.
  const r = owner.routing;
  const assignedIds = r?.enabled ? (r.assignedUserIds ?? []) : [];
  const assignmentOn = assignedIds.length > 0;
  const roster = ctx.users ? new Map(ctx.users.map((u) => [u.id, u])) : null;
  const reachableIds = roster
    ? assignedIds.filter((id) => roster.get(id)?.incomingCallEnabled === true)
    : assignedIds;
  const assignmentLive = assignmentOn && reachableIds.length > 0;
  const assignmentToVoicemail =
    assignmentOn && !assignmentLive && r?.whenUnavailable === 'voicemail';

  const everyone = ctx.users ? ctx.users.filter((u) => u.incomingCallEnabled).length : null;
  const everyoneText =
    everyone === null
      ? 'Everyone with incoming calls on'
      : `Everyone with incoming calls on (${everyone})`;

  const menuActive = ctx.menu.active;
  const menuHours = menuActive && !!ctx.menu.hoursEnabled;
  const hoursOn = ctx.phoneHours.enabled || menuHours;

  const steps: RoutingStep[] = [];

  // ── 1. Call flow ──
  steps.push(
    flow
      ? {
          key: 'flow',
          state: 'active',
          title: 'Call flow',
          detail: `${flow.name} decides everything that happens to the call${followedNote}.`,
          chip: flow.name,
          target: 'flows',
        }
      : {
          key: 'flow',
          state: 'skipped',
          title: 'Call flow',
          detail: 'No call flow is live on this number.',
          skipReason: 'No call flow is live on this number',
          chip: 'Call flow',
          target: 'flows',
        },
  );

  const byFlow = (reason: string) => ({ state: 'skipped' as const, skipReason: reason });

  // ── 2. Phone hours ──
  steps.push({
    key: 'hours',
    title: 'Phone hours',
    chip: 'Phone hours',
    target: 'general',
    ...(flow
      ? {
          ...byFlow('Skipped · the call flow sets its own hours'),
          detail: 'Not used on this number.',
        }
      : hoursOn
        ? {
            state: 'pass' as const,
            detail: menuHours
              ? 'Uses the phone menu’s hours. When you’re closed, callers hear your closed message.'
              : 'When you’re closed, callers hear your closed message.',
          }
        : {
            state: 'skipped' as const,
            skipReason: 'Off · the line answers around the clock',
            detail: 'Off · the line answers around the clock.',
          }),
  });

  // ── 3–4. Menu and Iris: skipped by a flow, a live assignment, or voicemail ──
  const numberDecides = assignmentLive
    ? 'this number rings its assigned people'
    : assignmentToVoicemail
      ? 'nobody assigned can answer, so calls go to voicemail'
      : null;

  if (menuActive) {
    steps.push({
      key: 'menu',
      title: 'Phone menu',
      chip: 'Phone menu',
      target: 'team',
      ...(flow
        ? { ...byFlow('Skipped · the call flow decides'), detail: 'Not used on this number.' }
        : numberDecides
          ? { ...byFlow(`Skipped · ${numberDecides}`), detail: 'Not used on this number.' }
          : {
              state: 'active' as const,
              detail: `${ctx.menu.name ? `${ctx.menu.name}: c` : 'C'}allers pick an option or dial an extension.`,
            }),
    });
  }

  steps.push({
    key: 'iris',
    title: 'Iris',
    chip: 'Iris',
    target: 'iris',
    ...(!ctx.iris.enabled
      ? {
          state: 'skipped' as const,
          skipReason: 'Off · calls ring your team instead',
          detail: 'Off · calls ring your team instead.',
        }
      : flow
        ? { ...byFlow('Skipped · the call flow runs instead'), detail: 'Not used on this number.' }
        : numberDecides
          ? { ...byFlow(`Skipped · ${numberDecides}`), detail: 'Not used on this number.' }
          : menuActive
            ? {
                ...byFlow('Skipped · the phone menu answers first'),
                detail: 'Not used on this number.',
              }
            : { state: 'active' as const, detail: 'Answers and greets every caller.' }),
  });

  // ── 5. Who rings ──
  const irisAnswers = ctx.iris.enabled && !flow && !numberDecides && !menuActive;
  const whoRings = ((): Omit<RoutingStep, 'key' | 'title' | 'target'> => {
    if (flow) {
      return {
        state: 'skipped',
        skipReason: 'Skipped · the call flow decides who rings',
        detail: 'Not used on this number.',
        chip: 'Who rings',
      };
    }
    if (assignmentToVoicemail) {
      return {
        state: 'active',
        detail: `Nobody assigned can take calls right now, so callers go to voicemail${followedNote}.`,
        chip: 'Voicemail',
      };
    }
    if (assignmentLive) {
      const names = roster ? reachableIds.map((id) => roster.get(id)?.name).filter(Boolean) : [];
      const who =
        names.length > 0 && names.length <= 3
          ? names.join(', ')
          : plural(reachableIds.length, 'person', 'people');
      return {
        state: 'active',
        detail: `Rings ${who}${deskText}${followedNote}.`,
        chip: plural(reachableIds.length, 'person', 'people'),
      };
    }
    if (menuActive) {
      return {
        state: 'skipped',
        skipReason: 'Skipped · the phone menu decides who rings',
        detail: 'The phone menu decides who rings.',
        chip: 'Everyone',
      };
    }
    const fallback = assignmentOn ? 'Nobody assigned can take calls right now, so: ' : '';
    const empty = everyone === 0 && deskPhones === 0;
    return {
      state: irisAnswers ? 'pass' : 'active',
      detail: empty
        ? 'Nobody has incoming calls on, and no desk phone rings for this number.'
        : irisAnswers
          ? `When Iris hands a caller over: ${everyoneText.toLowerCase()}${deskText}${followedNote}.`
          : `${fallback}${everyoneText}${deskText}${followedNote}.`,
      chip: followed ? `Same as ${formatNational(followed.phoneNumber)}` : 'Everyone',
    };
  })();
  steps.push({
    key: 'whoRings',
    title: 'Who rings',
    target: 'numbers',
    ...whoRings,
  });

  // ── 6. Nobody answers ──
  steps.push({
    key: 'noAnswer',
    title: 'If nobody answers',
    target: 'general',
    ...(flow
      ? flowEnding(flow) === 'voicemail'
        ? {
            state: 'active' as const,
            detail: `Voicemail, inside ${flow.name}.`,
            chip: 'Voicemail',
          }
        : flowEnding(flow) === 'hangup'
          ? {
              state: 'active' as const,
              detail: `${flow.name} hangs up.`,
              chip: 'Hangs up',
            }
          : {
              state: 'active' as const,
              detail: `${flow.name} decides.`,
              chip: 'Flow decides',
            }
      : assignmentToVoicemail
        ? {
            state: 'skipped' as const,
            skipReason: 'Skipped · callers already went to voicemail',
            detail: 'Callers already went to voicemail.',
            chip: 'Voicemail',
          }
        : ctx.voicemailAfterNoAnswer
          ? {
              state: 'active' as const,
              detail: 'Voicemail, then the missed-call text.',
              chip: 'Voicemail',
            }
          : {
              state: 'active' as const,
              detail:
                'The call ends. Voicemail and the missed-call text don’t run after an unanswered call yet.',
              chip: 'Call ends',
            }),
  });

  return steps;
}

/**
 * The path chips for a number: only the steps that actually take the call,
 * in order. A live flow is the whole path.
 */
export function routingPath(steps: RoutingStep[]): { key: RoutingStepKey; label: string }[] {
  const flow = steps.find((s) => s.key === 'flow' && s.state === 'active');
  if (flow) return [{ key: 'flow', label: flow.chip }];
  return steps.filter((s) => s.state !== 'skipped').map((s) => ({ key: s.key, label: s.chip }));
}

/** Numbers Iris won't answer while it is on, and why. Empty when Iris is off. */
export function irisExclusions(
  ctx: RoutingContext,
): { number: RoutingNumberInput; reason: string }[] {
  if (!ctx.iris.enabled) return [];
  return ctx.numbers.flatMap((number) => {
    const iris = resolveRouting(number, ctx).find((s) => s.key === 'iris');
    if (!iris || iris.state !== 'skipped') return [];
    return [{ number, reason: (iris.skipReason ?? '').replace(/^Skipped · /, '') }];
  });
}
